import { DrawerLayout } from "@/components/layouts/DrawerLayout.jsx";
import { useParams } from "react-router-dom";
import { useGetRecipesImportQuery } from "@/features/recipes/imports/api.js";
import { useEffect, useState, useRef } from "react";
import { Box, Typography, Card, CardContent, LinearProgress, Chip, Stack, Divider } from "@mui/joy";
import { ImportStatus, ImportItemStatus } from "@/features/recipes/imports/components/ImportStatuses";
import { ExternalLinkButton } from "@/components/ExternalLinkButton";
import { Alert } from "@mui/joy";
import { RecipeCard } from '@/features/recipes/components/RecipeCard';
import { baseApi } from '@/utils/api.js';

export function ViewRecipesImportPage() {
  const { householdId, id } = useParams();
  const [ hasPending, setHasPending ] = useState(false);
  const previousStatusRef = useRef(null);
  
  const { data, isLoading, error } = useGetRecipesImportQuery({ householdId, id }, { pollingInterval: hasPending ? 1000 : 0 });

  useEffect(() => {
    setHasPending(data?.data.status === 'pending' || data?.data.status === 'processing');
  }, [data]);

  useEffect(() => {
    const currentStatus = data?.data?.status;
    const previousStatus = previousStatusRef.current;
    
    if (currentStatus === 'finished' && previousStatus !== 'finished') {
      baseApi.util.invalidateTags([
        { type: 'Recipes', id: 'LIST', householdId },
      ]);
    }
    
    previousStatusRef.current = currentStatus;
  }, [data?.data?.status, householdId]);

  if (isLoading) {
    return (
      <DrawerLayout title="Import Details">
        <Box sx={{ p: 2 }}>
          <LinearProgress />
        </Box>
      </DrawerLayout>
    );
  }

  if (error) {
    return (
      <DrawerLayout title="Import Details">
        <Box sx={{ p: 2 }}>
          <Typography color="danger">Error loading import details</Typography>
        </Box>
      </DrawerLayout>
    );
  }

  const importData = data?.data;
  if (!importData) return null;

  return (
    <DrawerLayout title="Import Details">      
      <Stack spacing={3} sx={{ mt: 3, mx: -3 }}>            
        <Stack spacing={1} direction="column" sx={{ px: 3 }}>
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Typography level="title-md">Progress</Typography>
            <ImportStatus recipeImport={importData} />
          </Stack>
          <LinearProgress            
            determinate 
            color={importData.status === 'finished' ? (importData.errors > 0 ? 'danger' : 'success') : 'primary'}
            value={(importData.processed / importData.total) * 100}           
          />
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Typography level="body-sm">
              {importData.processed} of {importData.total} items processed
            </Typography>
          <Stack spacing={1} direction="row">
            {importData.success > 0 && <Chip color="success" variant="outlined">Success: {importData.success}</Chip>}
            {importData.errors > 0 && <Chip color="danger" variant="outlined">Errors: {importData.errors}</Chip>}
          </Stack>
          </Stack>
        </Stack>
    
        <Divider />

        <Stack spacing={1} sx={{ px: 3 }} justifyContent="space-between">                    
          <Typography level="body-sm">
            Started: {new Date(importData.started).toLocaleString()}
          </Typography>
          {importData.finished && <Typography level="body-sm">
            Finished: {new Date(importData.finished).toLocaleString()}
          </Typography>}
        </Stack>

        <Divider />

        <Stack spacing={1} direction="column" sx={{ px: 3 }}>
          <Typography level="title-md">Imported Items</Typography>          
          {importData.items.map((item) => (
            item.recipe_id ? <RecipeCard key={item.id} recipe={toRecipe(item)} orientation="horizontal" /> :
            <Card key={item.id} variant="outlined" color="neutral">
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <ExternalLinkButton url={item.url} maxWidth="300px" />                    
                    <ImportItemStatus recipeImportItem={item} />
                  </Stack>
                  {item.error && (
                    <Alert color="danger" level="body-sm">
                      Error: {item.error}
                    </Alert>
                  )}                  
                </Stack>
              </CardContent>
            </Card>
          ))}  
        </Stack>      
      </Stack>      
    </DrawerLayout>
  );
}

function toRecipe(item) {
  return {
    id: item.recipe_id,
    name: item.recipe_name,
    images: item.recipe_images,
    prep_time: item.recipe_prep_time,
    cook_time: item.recipe_cook_time,
    tags: item.recipe_tags,
  };
}
